import type { BestFareByDate } from "@jetblue/dotcom-api/best-fares";
import type { DayRenderProperties } from "@jetblue/kit/calendar";
import { VisuallyHidden } from "@jetblue/kit/visually-hidden";
import { clsx } from "clsx";
import {
  formatCompactPoints,
  formatCurrency,
  formatPoints,
} from "./format-price";

const formatPrice = (
  amount: number,
  shouldUsePoints: boolean,
  currencyCode?: string,
) => {
  if (shouldUsePoints) {
    return formatCompactPoints(amount);
  }

  return formatCurrency(amount, currencyCode);
};

const formatAccessibilityPrice = (
  amount: number,
  shouldUsePoints: boolean,
  currencyCode?: string,
) => {
  if (shouldUsePoints) {
    return `${formatPoints(amount)} points`;
  }

  return formatCurrency(amount, currencyCode);
};

type BestFaresDayRenderProperties = Pick<
  DayRenderProperties,
  "date" | "isActive" | "isDisabled"
>;

export type BestFaresDateCellProperties = BestFaresDayRenderProperties & {
  currencyCode?: string | undefined;
  fare?: BestFareByDate | undefined;
  isLoading: boolean;
  isLowestFare?: boolean | undefined;
  message?: string | undefined;
  usePoints: boolean;
};
export const BestFaresDateCell = ({
  currencyCode,
  date,
  fare,
  isActive,
  isDisabled,
  isLoading,
  isLowestFare,
  message,
  usePoints,
}: BestFaresDateCellProperties) => {
  if (!date) {
    return null;
  }

  const dayClassName = clsx({
    "text-foreground-on-primary": isActive,
    "text-foreground-primary": !isDisabled && !isActive,
    "text-foreground-surface-container-variant-medium": isDisabled,
  });

  const priceClassName = clsx("text-caption", {
    "font-bold": isLowestFare,
    "text-foreground-on-primary": isActive,
    "text-foreground-on-surface": !isDisabled && !isActive,
    "text-foreground-surface-container-variant-medium": isDisabled,
  });

  const isShowPriceSkeleton = isLoading && !fare && !isDisabled;
  const showPrice = fare && !isDisabled;
  const accessibilityDescription =
    fare && message
      ? `${formatAccessibilityPrice(fare.amount, usePoints, currencyCode)}. ${message}`
      : undefined;

  return (
    <>
      <span className="flex flex-col items-center leading-tight">
        <span className={dayClassName}>{date.day}</span>
        {isShowPriceSkeleton && (
          <span
            className="
              mt-0.5 h-2 w-7 animate-pulse rounded-sm
              bg-background-surface-container-variant-low
            "
          />
        )}
        {showPrice && (
          <span className={priceClassName}>
            {formatPrice(fare.amount, usePoints, currencyCode)}
          </span>
        )}
      </span>
      {accessibilityDescription ? (
        <VisuallyHidden>{accessibilityDescription}</VisuallyHidden>
      ) : null}
    </>
  );
};
